import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { useMiniQuestionnaire } from "@/hooks/useMiniQuestionnaire";
import MiniSection1 from "@/components/mini-questionnaire/MiniSection1";
import MiniSection2 from "@/components/mini-questionnaire/MiniSection2";
import MiniSection3 from "@/components/mini-questionnaire/MiniSection3";
import MiniSection4 from "@/components/mini-questionnaire/MiniSection4";
import MiniSection5 from "@/components/mini-questionnaire/MiniSection5";
import MiniSection6 from "@/components/mini-questionnaire/MiniSection6";
import MiniSection7 from "@/components/mini-questionnaire/MiniSection7";
import Header from "@/components/Header";

const TOTAL_SECTIONS = 7;

const MiniQuestionnaire = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(1);
  const { data, updateData, updateDepistages } = useMiniQuestionnaire();

  const handleNext = () => {
    if (currentSection < TOTAL_SECTIONS) {
      setCurrentSection(currentSection + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Sauvegarder les données et rediriger vers les résultats
      localStorage.setItem("miniQuestionnaireData", JSON.stringify(data));
      navigate("/mini-resultats");
    }
  };

  const handlePrevious = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const progress = (currentSection / TOTAL_SECTIONS) * 100;

  const renderSection = () => {
    switch (currentSection) {
      case 1:
        return <MiniSection1 data={data} updateData={updateData} />;
      case 2:
        return <MiniSection2 data={data} updateData={updateData} />;
      case 3:
        return <MiniSection3 data={data} updateData={updateData} />;
      case 4:
        return <MiniSection4 data={data} updateData={updateData} />;
      case 5:
        return <MiniSection5 data={data} updateData={updateData} />;
      case 6:
        return <MiniSection6 data={data} updateData={updateData} />;
      case 7:
        return <MiniSection7 data={data} updateDepistages={updateDepistages} />;
      default:
        return null;
    }
  };

  const getSectionTitle = () => {
    switch (currentSection) {
      case 1: return "Questions initiales";
      case 2: return "Activité physique";
      case 3: return "Tabac";
      case 4: return "Alcool";
      case 5: return "Nutrition";
      case 6: return "Sommeil";
      case 7: return "Dépistage";
      default: return "";
    }
  };

  const isNextDisabled = () => {
    switch (currentSection) {
      case 1:
        return !data.age || !data.sexe || !data.tabac_regulier;
      case 2:
        return !data.renforcement_musculaire;
      case 3:
        if (!data.fume_actuellement) return true;
        if (data.fume_actuellement === "oui") {
          return !data.annees_tabagisme || !data.cigarettes_par_jour;
        }
        return !data.exposition_tabagisme_passif;
      case 4:
        return !data.frequence_alcool;
      case 5:
        return !data.viande_rouge_frequence || !data.regime_mediterraneen;
      case 6:
        return !data.heures_sommeil;
      case 7:
        return false; // Les dépistages sont optionnels
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-feel-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-feel-secondary-foreground">
              Section {currentSection} sur {TOTAL_SECTIONS}
            </span>
            <span className="text-sm font-medium text-feel-primary">
              {getSectionTitle()}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Section Content */}
        <div className="bg-feel-card rounded-xl shadow-lg p-6 md:p-8 mb-8">
          {renderSection()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentSection === 1}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Précédent
          </Button>

          <Button
            onClick={handleNext}
            disabled={isNextDisabled()}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {currentSection === TOTAL_SECTIONS ? (
              <>
                Voir mon bilan
                <CheckCircle className="w-4 h-4" />
              </>
            ) : (
              <>
                Suivant
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default MiniQuestionnaire;
